// MaturaParallelLinesParameterClosedTask.js
// Zadanie maturalne zamknięte: parametr w równaniach prostych równoległych.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = (arr) => arr[randInt(0, arr.length - 1)];

function randNonZero(min, max) {
  let value = 0;
  while (value === 0) value = randInt(min, max);
  return value;
}

function shuffle(items) {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function linearLatex(a, b) {
  const mPart = a === 1 ? "m" : a === -1 ? "-m" : `${a}m`;
  if (b === 0) return mPart;
  return b > 0 ? `${mPart}+${b}` : `${mPart}-${Math.abs(b)}`;
}

function slopeLatex(slope) {
  return slope.hasParam ? linearLatex(slope.a, slope.b) : String(slope.value);
}

function interceptLatex(intercept) {
  return intercept.hasParam ? linearLatex(intercept.a, intercept.b) : String(intercept.value);
}

function xTermLatex(slope) {
  const s = slopeLatex(slope);
  if (s === "1") return "x";
  if (s === "-1") return "-x";
  return slope.hasParam ? `(${s})x` : `${s}x`;
}

function plusPartLatex(valueLatex) {
  if (valueLatex === "0") return "";
  if (valueLatex.startsWith("-")) return `-${valueLatex.slice(1)}`;
  return `+${valueLatex}`;
}

function lineLatex(line) {
  const xTerm = xTermLatex(line.slope);
  const intercept = interceptLatex(line.intercept);
  if (line.intercept.hasParam) return `y=${xTerm}+(${intercept})`;
  return `y=${xTerm}${plusPartLatex(intercept)}`;
}

function makeConst(value) {
  return { hasParam: false, value };
}

function makeParam(a, b) {
  return { hasParam: true, a, b };
}

// Shared by original papers with the same parallel-lines parameter mechanism.
export function solveParallelSlopes(kSlope, lSlope) {
  const coefficient = s => s.hasParam ? s.a : 0;
  const constant = s => s.hasParam ? s.b : s.value;
  const denominator = coefficient(kSlope) - coefficient(lSlope);
  if (!denominator) throw new Error("Parallel slopes do not determine one parameter");
  return (constant(lSlope) - constant(kSlope)) / denominator;
}

function buildOptions(answer, traps = []) {
  const values = [];
  const add = (v) => {
    if (!Number.isInteger(v)) return;
    if (!values.includes(v)) values.push(v);
  };

  add(answer);
  traps.forEach(add);
  add(answer + 2);
  add(answer - 2);
  add(-answer);
  add(0);
  add(answer + 1);
  add(answer - 1);

  return shuffle(values.slice(0, 4)).map((value, idx) => ({
    label: LETTERS[idx],
    value,
    latex: String(value),
    isCorrect: value === answer,
  }));
}

function pickTargetSlope() {
  return randNonZero(-5, 5);
}

function pickParamSlopeForAnswer(answer, targetSlope) {
  for (let attempt = 0; attempt < 80; attempt += 1) {
    const a = randNonZero(-4, 4);
    const b = targetSlope - a * answer;
    if (b >= -9 && b <= 9) return makeParam(a, b);
  }

  return makeParam(1, targetSlope - answer);
}

function generateOneParamSlope(pattern) {
  const answer = randInt(-5, 5);
  const targetSlope = pickTargetSlope();
  const paramSlope = pickParamSlopeForAnswer(answer, targetSlope);
  const paramIntercept = makeParam(randNonZero(-3, 3), randInt(-5, 5));
  const constInterceptA = makeConst(randNonZero(-6, 6));
  const constInterceptB = makeConst(randNonZero(-6, 6));

  const k =
    pattern === "kSlope"
      ? { slope: paramSlope, intercept: constInterceptA }
      : { slope: makeConst(targetSlope), intercept: paramIntercept };
  const l =
    pattern === "kSlope"
      ? { slope: makeConst(targetSlope), intercept: paramIntercept }
      : { slope: paramSlope, intercept: constInterceptB };

  const interceptTrap =
    paramIntercept.a !== 0
      ? Math.round(((pattern === "kSlope" ? constInterceptA.value : constInterceptB.value) - paramIntercept.b) / paramIntercept.a)
      : null;

  return {
    k,
    l,
    answer: solveParallelSlopes(k.slope, l.slope),
    equationLatex: `${slopeLatex(paramSlope)}=${targetSlope}`,
    traps: [targetSlope, interceptTrap],
  };
}

function generateBothSlopes() {
  const answer = randInt(-5, 5);
  const a1 = randNonZero(-4, 4);
  let a2 = randNonZero(-4, 4);
  while (a2 === a1) a2 = randNonZero(-4, 4);

  const b1 = randInt(-6, 6);
  const b2 = (a1 - a2) * answer + b1;

  if (b2 < -12 || b2 > 12) return generateBothSlopes();

  const k = { slope: makeParam(a1, b1), intercept: makeConst(randNonZero(-6, 6)) };
  const l = { slope: makeParam(a2, b2), intercept: makeConst(randNonZero(-6, 6)) };

  return {
    k,
    l,
    answer: solveParallelSlopes(k.slope, l.slope),
    equationLatex: `${slopeLatex(k.slope)}=${slopeLatex(l.slope)}`,
    traps: [b2 - b1, -answer],
  };
}

/**
 * Wariant „oryginał” – matura podstawowa, maj 2025, zadanie 23:
 *   k: y=(m−2)x+5,  l: y=−4x+(m+3).  Równoległe ⟺ m−2=−4 ⟹ m=−2  (B).
 */
function createMay2025OriginalTask() {
  const k = { slope: makeParam(1, -2), intercept: makeConst(5) };
  const l = { slope: makeConst(-4), intercept: makeParam(1, 3) };
  const options = [
    { label: "A", value: -4, latex: "-4", isCorrect: false },
    { label: "B", value: -2, latex: "-2", isCorrect: true },
    { label: "C", value: 2, latex: "2", isCorrect: false },
    { label: "D", value: 5, latex: "5", isCorrect: false },
  ];

  return {
    type: "MaturaParallelLinesParameterClosedTask",
    data: {
      k,
      l,
      answer: -2,
      equationLatex: "m-2=-4",
      traps: [-4, 2],
      pattern: "kSlope",
      options,
      correctIndex: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 23,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if(generationOptions.variant==="original"&&generationOptions.session==="2023_CZE"){
    const k={slope:{hasParam:true,a:2,b:3},intercept:{hasParam:false,value:5}},l={slope:{hasParam:false,value:-1},intercept:{hasParam:false,value:0}},options=[-2,-1,1,2].map((n,i)=>({label:"ABCD"[i],latex:String(n),isCorrect:i===0}));
    return {type:"MaturaParallelLinesParameterClosedTask",data:{k,l,answer:solveParallelSlopes(k.slope,l.slope),equationLatex:"2m+3=-1",options,correctIndex:0},source:{variant:"original",exam:"MATURA",session:"2023_CZE",taskNumber:"11"}};
  }
  if(generationOptions.variant==="original"&&generationOptions.session==="2023_GRU"){
    const k={slope:makeConst(-.5),intercept:makeConst(-7)},l={slope:makeParam(2,-1),intercept:makeConst(13)};
    const options=["-\\frac12","\\frac14","\\frac32","2"].map((latex,i)=>({label:LETTERS[i],latex,isCorrect:i===1}));
    return {type:"MaturaParallelLinesParameterClosedTask",data:{k,l,answer:solveParallelSlopes(k.slope,l.slope),equationLatex:"2m-1=-\\frac12",options,correctIndex:1},source:{variant:"original",exam:"MATURA",session:"2023_GRU",taskNumber:"19"}};
  }
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return createMay2025OriginalTask();
  }

  const pattern = pick(["kSlope", "lSlope", "bothSlopes"]);
  const generated =
    pattern === "bothSlopes"
      ? generateBothSlopes()
      : generateOneParamSlope(pattern);

  const options = buildOptions(generated.answer, generated.traps);
  const correctIndex = options.findIndex((option) => option.isCorrect);

  return {
    type: "MaturaParallelLinesParameterClosedTask",
    data: {
      ...generated,
      pattern,
      options,
      correctIndex,
    },
  };
}

export function renderTask({ data }) {
  const { k, l, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte (0-1 pkt).</strong>
      </p>

      <p>
        W kartezjańskim układzie współrzędnych <InlineMath math="(x,y)" /> proste{" "}
        <InlineMath math="k" /> oraz <InlineMath math="l" /> są określone równaniami
      </p>

      <div style={{ textAlign: "center", lineHeight: 1.9 }}>
        <div>
          <InlineMath math={`k:\\; ${lineLatex(k)}`} />
        </div>
        <div>
          <InlineMath math={`l:\\; ${lineLatex(l)}`} />
        </div>
      </div>

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>

      <p>
        Proste <InlineMath math="k" /> oraz <InlineMath math="l" /> są równoległe, gdy liczba{" "}
        <InlineMath math="m" /> jest równa
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((option) => (
          <div key={option.label} style={{ marginBottom: 8 }}>
            <InlineMath math={`${option.label}.\\; ${option.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex = userAnswer && typeof userAnswer.choiceIndex === "number" ? userAnswer.choiceIndex : null;
  const letterOptions = data.options.map((option, idx) => ({
    value: idx,
    label: option.label,
    textNode: option.label,
  }));

  const handleChange = (idx) => {
    const option = data.options[idx];
    onChange?.({
      choiceIndex: idx,
      label: option.label,
      latex: option.latex,
      value: option.value,
    });
  };

  return <ABCDInput options={letterOptions} value={choiceIndex} onChange={handleChange} disabled={disabled} />;
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options?.[data.correctIndex];
  if (!correct) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number" || !userAnswer.label || !userAnswer.latex) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { k, l, equationLatex, answer } = data;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Proste są równoległe, gdy mają równe współczynniki kierunkowe.
      </p>

      <p>
        Z równania prostej <InlineMath math="y=ax+b" /> współczynnikiem kierunkowym jest liczba stojąca przy{" "}
        <InlineMath math="x" />.
      </p>

      <BlockMath math={`k:\\; ${lineLatex(k)}`} />
      <BlockMath math={`l:\\; ${lineLatex(l)}`} />

      <p>
        <strong>Krok 2.</strong> Porównujemy współczynniki kierunkowe, ignorując wyrazy wolne:
      </p>

      <BlockMath math={equationLatex} />

      <p>
        <strong>Krok 3.</strong> Rozwiązujemy równanie i otrzymujemy:
      </p>

      <BlockMath math={`m=${answer}`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};
